import { Fragment, useState } from 'react'
import { api } from '../api/client'
import type { AppUser, ProjectDetail, ProjectRole, Role } from '../api/types'
import { formatDateTime } from '../lib/time'
import { useAsync } from '../lib/useAsync'
import { canChangeRole, heldRole, projectRoleChoices } from '../lib/userProjectRoles'

const ROLES: Role[] = ['pending', 'viewer', 'admin', 'disabled']

const ROLE_NOTE: Record<Role, string> = {
  pending: 'signed in, sees nothing yet',
  viewer: 'everything except this panel',
  admin: 'everything, including this panel',
  disabled: 'cannot sign in',
}

interface Props {
  users: AppUser[]
  me: number | undefined
  busy: boolean
  onSetRole: (id: number, role: Role) => void
}

/** Who can sign in, what they see, and the role each holds in each project.
 *
 *  Status is text, not colour: docs/DESIGN_GUIDELINE.md §2.6 has no approved
 *  status colours yet. Pending users come first from the API because they are
 *  the ones waiting on someone. */
export function UserAdmin({ users, me, busy, onSetRole }: Props) {
  // Every project with its people, read once and again after each change.
  const projects = useAsync(async () => {
    const list = await api.projects.list()
    return Promise.all(list.projects.map((p) => api.projects.get(p.id)))
  }, [])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const changeRole = async (project: number, person: number, role: '' | ProjectRole) => {
    setSaving(true); setError('')
    try { await api.projects.setUserRole(project, person, role || null); projects.reload() }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'The project role could not be saved.') }
    finally { setSaving(false) }
  }
  if (users.length === 0) return <p className="muted">Nobody has signed in yet.</p>
  return (
    <table className="grid">
      <thead>
        <tr><th>User</th><th>Platform role</th><th>Project roles</th><th>Last seen</th><th>Since</th></tr>
      </thead>
      <tbody>
        {users.map((user) => (
          <Fragment key={user.id}>
            <tr>
              <td>
                <strong>{user.display_name ?? user.email}</strong>
                {user.id === me && <span className="pill neutral">you</span>}
                <br /><span className="muted">{user.email}</span>
              </td>
              <td>
                <select value={user.role} disabled={busy}
                        aria-label={`Role for ${user.email}`}
                        onChange={(e) => onSetRole(user.id, e.target.value as Role)}>
                  {ROLES.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
                {user.role === 'pending' && <span className="pill warn">needs approval</span>}
                <br /><small className="muted">{ROLE_NOTE[user.role]}</small>
              </td>
              <td>
                <ProjectRoles user={user} projects={projects.data} loading={projects.loading}
                              error={error || projects.error} disabled={busy || saving}
                              onChange={(project, role) => void changeRole(project, user.id, role)} />
              </td>
              <td className="muted nowrap">
                {user.last_seen_at ? formatDateTime(user.last_seen_at) : '—'}
              </td>
              <td className="muted nowrap">{formatDateTime(user.created_at)}</td>
            </tr>
          </Fragment>
        ))}
      </tbody>
    </table>
  )
}

/** One person's role in each project. A project role decides what they may
 *  change in that project (a Viewer reads and adds logs, chat and readings by
 *  hand, an Editor changes its stations and details, an Admin its people); it
 *  never limits which stations they see, since every Viewer sees every station.
 *  The list is drawn once opened: it is a select per project, for every user. */
function ProjectRoles({ user, projects, loading, error, disabled, onChange }: {
  user: AppUser; projects: ProjectDetail[] | undefined; loading: boolean
  error: string | undefined; disabled: boolean
  onChange: (project: number, role: '' | ProjectRole) => void
}) {
  const [open, setOpen] = useState(false)
  const held = (projects ?? []).filter((p) => heldRole(p.members, user.id) !== null).length
  return (
    <details onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>{loading && !projects ? 'Project roles' : `Project roles (${held})`}</summary>
      {open && <>
        <p className="muted small">
          A project role decides what someone can change in that project. Every viewer sees every station.
          {user.role === 'admin' && ' A platform admin manages all projects; these are explicit memberships.'}
        </p>
        {error && <p className="error" role="alert">{error}</p>}
        {loading && !projects && <p className="muted small">Loading…</p>}
        {(projects ?? []).map((p) => {
          const role = heldRole(p.members, user.id)
          return (
            <label className="user-project-role" key={p.project.id}>
              <span>{p.project.name}</span>
              <select aria-label={`${user.email} role in ${p.project.name}`} value={role ?? ''}
                      disabled={disabled || !canChangeRole(user.role, role)}
                      onChange={(event) => onChange(p.project.id, event.target.value as '' | ProjectRole)}>
                {projectRoleChoices(user.role, role).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
          )
        })}
      </>}
    </details>
  )
}
